/**
 * Comments Service
 * 
 * Manages trip-level comments
 */

import {
  collection,
  doc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  onSnapshot,
  query,
  orderBy,
  Timestamp,
  serverTimestamp,
  setDoc,
} from 'firebase/firestore';
import { Platform } from 'react-native';
import { db } from '@/lib/firebase';
import { syncQueue, type SyncAction } from '@/services/syncQueue';
import { networkMonitor } from '@/services/networkMonitor';

export type Comment = {
  id: string;
  tripId: string;
  userId: string;
  userName: string;
  text: string;
  createdAt: Date;
  updatedAt: Date;
};

const isOfflineLikeErrorCode = (code?: string): boolean =>
  code === 'unavailable' ||
  code === 'failed-precondition' ||
  code === 'deadline-exceeded' ||
  code === 'aborted';

const shouldQueueOffline = (bypassOffline?: boolean): boolean =>
  !bypassOffline && networkMonitor.shouldUseOfflineDataFallback();

type FirestoreComment = {
  userId: string;
  userName: string;
  text: string;
  createdAt: Timestamp | Date;
  updatedAt: Timestamp | Date;
};

// Convert Firestore comment to app Comment type
const convertFirestoreComment = (id: string, data: FirestoreComment): Comment => {
  const convertDate = (date: Timestamp | Date | undefined): Date => {
    if (!date) return new Date();
    if (date instanceof Timestamp) return date.toDate();
    if (date instanceof Date) return date;
    return new Date();
  };

  return {
    id,
    tripId: '', // Will be set by caller
    userId: data.userId,
    userName: data.userName,
    text: data.text,
    createdAt: convertDate(data.createdAt),
    updatedAt: convertDate(data.updatedAt),
  };
};

const NATIVE_COMMENTS_POLL_MS = 30000;

export const fetchTripComments = async (tripId: string): Promise<Comment[]> => {
  const commentsRef = collection(db, 'trips', tripId, 'comments');
  const q = query(commentsRef, orderBy('createdAt', 'desc'));
  const snapshot = await getDocs(q);
  return snapshot.docs.map((docSnap) => {
    const comment = convertFirestoreComment(docSnap.id, docSnap.data() as FirestoreComment);
    comment.tripId = tripId;
    return comment;
  });
};

/**
 * Subscribe to trip comments
 */
export const subscribeToTripComments = (
  tripId: string,
  callback: (comments: Comment[]) => void
): (() => void) => {
  if (Platform.OS !== 'web') {
    let isCleanedUp = false;
    const poll = async () => {
      try {
        const comments = await fetchTripComments(tripId);
        if (!isCleanedUp) {
          callback(comments);
        }
      } catch (error) {
        console.error('[Comments] Native polling fetch failed:', error);
        if (!isCleanedUp) {
          callback([]);
        }
      }
    };
    void poll();
    const interval = setInterval(() => {
      void poll();
    }, NATIVE_COMMENTS_POLL_MS);
    return () => {
      isCleanedUp = true;
      clearInterval(interval);
    };
  }

  const commentsRef = collection(db, 'trips', tripId, 'comments');
  const q = query(commentsRef, orderBy('createdAt', 'desc'));
  let isCleanedUp = false;
  let unsubscribeFn: (() => void) | null = null;

  try {
    unsubscribeFn = onSnapshot(
      q,
      (snapshot) => {
        if (isCleanedUp) {
          return;
        }
        setTimeout(() => {
          if (isCleanedUp) {
            return;
          }
          try {
            const comments: Comment[] = snapshot.docs.map((docSnap) => {
              const data = docSnap.data() as FirestoreComment;
              const comment = convertFirestoreComment(docSnap.id, data);
              comment.tripId = tripId;
              return comment;
            });
            callback(comments);
          } catch (error) {
            console.error('[Comments] Error processing snapshot:', error);
            if (!isCleanedUp) {
              callback([]);
            }
          }
        }, 0);
      },
      (error) => {
        if (isCleanedUp) {
          return;
        }
        console.error('[Comments] Error subscribing to comments:', error);
        callback([]);
      },
    );
  } catch (error) {
    console.error('[Comments] Error setting up comments subscription:', error);
    callback([]);
  }

  return () => {
    isCleanedUp = true;
    if (unsubscribeFn) {
      try {
        unsubscribeFn();
      } catch {
        // ignore cleanup errors
      }
      unsubscribeFn = null;
    }
  };
};

/**
 * Create a new comment
 */
export const createComment = async (
  tripId: string,
  userId: string,
  userName: string,
  text: string,
  options?: { bypassOffline?: boolean; presetCommentId?: string },
): Promise<string> => {
  const normalizedText = text.trim();
  try {
    if (!normalizedText) {
      throw new Error('Comment text cannot be empty');
    }

    if (!tripId || !userId || !userName) {
      throw new Error('Missing required fields: tripId, userId, or userName');
    }

    if (shouldQueueOffline(options?.bypassOffline)) {
      const tempId =
        options?.presetCommentId ??
        `offline_comment_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
      await syncQueue.queueAction({
        type: 'create_comment',
        payload: {
          tripId,
          userId,
          userName,
          text: normalizedText,
          tempId,
        },
        tripId,
        userId,
      });
      return tempId;
    }

    console.log('[Comments] Creating comment', { textLength: normalizedText.length });

    const commentsRef = collection(db, 'trips', tripId, 'comments');
    const commentRef = options?.presetCommentId
      ? doc(commentsRef, options.presetCommentId)
      : doc(commentsRef);
    await setDoc(commentRef, {
      userId,
      userName,
      text: normalizedText,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });

    console.log('[Comments] Comment created successfully:', commentRef.id);
    return commentRef.id;
  } catch (error: any) {
    console.error('[Comments] Error creating comment:', error);
    if (isOfflineLikeErrorCode(error?.code) && !options?.bypassOffline) {
      const tempId = `offline_comment_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
      await syncQueue.queueAction({
        type: 'create_comment',
        payload: {
          tripId,
          userId,
          userName,
          text: normalizedText,
          tempId,
        },
        tripId,
        userId,
      });
      return tempId;
    }
    const errorMessage = error?.message || 'Failed to create comment';
    console.error('[Comments] Error details:', {
      code: error?.code,
      message: errorMessage,
      tripId,
      userId,
    });
    throw new Error(errorMessage);
  }
};

/**
 * Update a comment
 */
export const updateComment = async (
  tripId: string,
  commentId: string,
  text: string,
  options?: { bypassOffline?: boolean },
): Promise<void> => {
  const normalizedText = text.trim();
  try {
    if (!normalizedText) {
      throw new Error('Comment text cannot be empty');
    }

    if (shouldQueueOffline(options?.bypassOffline)) {
      await syncQueue.queueAction({
        type: 'update_comment',
        payload: {
          tripId,
          commentId,
          text: normalizedText,
        },
        tripId,
      });
      return;
    }

    const commentRef = doc(db, 'trips', tripId, 'comments', commentId);
    await updateDoc(commentRef, {
      text: normalizedText,
      updatedAt: serverTimestamp(),
    });
  } catch (error: any) {
    console.error('[Comments] Error updating comment:', error);
    if (isOfflineLikeErrorCode(error?.code) && !options?.bypassOffline) {
      await syncQueue.queueAction({
        type: 'update_comment',
        payload: {
          tripId,
          commentId,
          text: normalizedText,
        },
        tripId,
      });
      return;
    }
    throw error;
  }
};

/**
 * Delete a comment
 */
export const deleteComment = async (
  tripId: string,
  commentId: string,
  options?: { bypassOffline?: boolean },
): Promise<void> => {
  try {
    if (shouldQueueOffline(options?.bypassOffline)) {
      await syncQueue.queueAction({
        type: 'delete_comment',
        payload: {
          tripId,
          commentId,
        },
        tripId,
      });
      return;
    }
    const commentRef = doc(db, 'trips', tripId, 'comments', commentId);
    await deleteDoc(commentRef);
  } catch (error: any) {
    console.error('[Comments] Error deleting comment:', error);
    if (isOfflineLikeErrorCode(error?.code) && !options?.bypassOffline) {
      await syncQueue.queueAction({
        type: 'delete_comment',
        payload: {
          tripId,
          commentId,
        },
        tripId,
      });
      return;
    }
    throw error;
  }
};

syncQueue.registerHandler('create_comment', async (action: SyncAction) => {
  const { tripId, userId, userName, text, tempId } = action.payload as {
    tripId: string;
    userId: string;
    userName: string;
    text: string;
    tempId?: string;
  };
  await createComment(tripId, userId, userName, text, {
    bypassOffline: true,
    presetCommentId: tempId,
  });
});

syncQueue.registerHandler('update_comment', async (action: SyncAction) => {
  const { tripId, commentId, text } = action.payload as {
    tripId: string;
    commentId: string;
    text: string;
  };
  await updateComment(tripId, commentId, text, { bypassOffline: true });
});

syncQueue.registerHandler('delete_comment', async (action: SyncAction) => {
  const { tripId, commentId } = action.payload as {
    tripId: string;
    commentId: string;
  };
  await deleteComment(tripId, commentId, { bypassOffline: true });
});

